<template>
  <!-- Redirect Loading Overlay -->
  <Teleport :to="teleportTo ?? 'body'">
    <Transition name="fade">
      <div v-if="isRedirecting" class="redirect-overlay">
        <div class="redirect-loader">
          <div class="redirect-spinner" />
          <span class="redirect-text">{{ $t('common.loading') }}</span>
        </div>
      </div>
    </Transition>
  </Teleport>

  <Teleport :to="teleportTo ?? 'body'" :disabled="embedded || inline">
    <div v-if="modelValue && !isRedirecting"
class="modal-overlay"
:data-theme="light ? 'light' : undefined"
:class="[{ 'modal-overlay--embedded': embedded, 'modal-overlay--inline': inline }, embedded ? `modal-overlay--${step}` : '']"
@mousedown="!embedded && !inline && handleOverlayMouseDown($event)"
@mouseup="!embedded && !inline && handleOverlayMouseUp($event)">
    <div
      ref="modalContent"
      class="modal-content"
      :data-theme="light ? 'light' : undefined"
      :data-auth-mode="mode ?? 'login'"
      :role="embedded || inline ? undefined : 'dialog'"
      :aria-modal="embedded || inline ? undefined : true"
      :aria-label="$t(mode === 'register' ? 'auth.register' : 'auth.login')"
      @keydown="handleDialogKeydown"
    >
      <button
        v-if="!embedded && !inline"
        type="button"
        class="modal-close"
        :aria-label="$t('common.close')"
        @click="close"
      >
        <i class="zmdi zmdi-close" />
      </button>

      <!-- Капча живёт вне шагов. 11011 (CAPTCHA_SESSION_EXPIRED) прилетает и
           на шаге пароля, а виджет, вложенный в форму e-mail-шага, к тому
           моменту уже размонтирован: captchaRef === null, и вся обработка
           ошибки схлопывалась в опциональную цепочку, то есть в тишину. -->
      <div class="captcha-hidden-container">
        <CaptchaWidget
          ref="captchaRef"
          @verified="onCaptchaVerified"
          @expired="onCaptchaExpired"
          @error="onCaptchaError"
          @cancelled="onCaptchaCancelled"
        />
      </div>

      <!-- Step 1: Email Input -->
      <TelegramDeliveryRecovery
        v-if="step === 'delivery-recovery'"
        :recovery="telegramRecovery"
        :busy="isLoading || isResending || pendingCaptchaAction === 'send_code'"
        :error-message="errorMessage"
        @retry="retryTelegramDelivery"
        @back="goBackToEmail"
      />
      <template v-else-if="step === 'email'">
        <h2 v-if="!embedded" class="modal-title">{{ $t(mode === 'register' ? 'auth.register' : 'auth.login') }}</h2>
        <form @submit.prevent="handleEmailSubmit" class="auth-form">
          <div class="form-group">
            <label class="form-label" for="entry-email">{{ $t('auth.email') }}</label>
            <div class="email-input-container">
              <BaseInput bare
                id="entry-email"
                v-model="form.email"
                autocomplete="username"
                type="email" 
                class="form-input" 
                :placeholder="$t('auth.enterEmail')"
                required
                @focus="isEmailFocused = true"
                @blur="handleEmailBlur"
              />
              <EmailDomainSuggestions
                :email="form.email"
                :is-focused="isEmailFocused"
                @select="handleEmailSuggestionSelect"
              />
            </div>
          </div>

          <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading">
            {{ isLoading ? $t('auth.verifying') : $t('auth.continue') }}
          </BaseButton>

          <div v-if="!embedded" class="divider"><span>{{ $t('auth.or') }}</span></div>

          <p class="modal-subtitle">{{ $t('emailLink.telegramLogin') }}</p>
          <SocialAuthButtons
            mode="login"
            :light="light"
            :inviter-login="form.inviterLogin"
            @totp-required="handleTelegramTotp"
          />
        </form>
      </template>

      <!-- Спринт 14: пока канал не выбран, экрана ввода кода нет -->
      <template v-else-if="step === 'choice'">
        <OtpChannelChoice
          :channels="choiceChannels"
          :busy="isLoading"
          @pick="handleChoicePick"
        />
        <p v-if="errorMessage" class="form-error" v-html="errorMessage" />
      </template>

      <!-- Step 2a: Code Verification (existing user) -->
      <template v-else-if="step === 'code'">
        <h2 class="modal-title">{{ $t('auth.checkInbox') }}</h2>
        
        <p class="modal-subtitle">
          <template v-if="codeReceiver === 'telegram'">{{ $t('auth.otpChoice.sentToTelegram') }}</template>
          <template v-else>{{ $t('auth.codeSent') }} <strong>{{ receiverAddress || form.email }}</strong>.</template>
        </p>

        <div v-if="canSwitchChannel" class="resend-link">
          <a href="#" data-test="switch-channel" @click.prevent="switchChannel">
            {{ codeReceiver === 'telegram' ? $t('auth.otpChoice.switchToEmail') : $t('auth.otpChoice.switchToTelegram') }}
          </a>
        </div>

        <!-- Migration Notice -->
        <div v-if="migrationDomain" class="migration-notice">
          <i class="zmdi zmdi-info-outline" />
          <span>{{ $t('auth.accountMigrated', { domain: migrationDomain }) }}</span>
        </div>

        <form @submit.prevent="handleCodeSubmit" class="auth-form">
          <div class="form-group">
            <div v-if="sessionNumber" class="session-number">
              {{ $t('auth.session', { number: sessionNumber }) }}
            </div>
            <label class="form-label">{{ $t('auth.enterCode') }}</label>
            <div class="code-digits">
              <input
                v-for="(_, index) in 6"
                :key="index"
                ref="codeInputs"
                type="text"
                inputmode="numeric"
                maxlength="1"
                v-model="codeDigits[index]"
                @input="handleCodeInput($event, index)"
                @keydown.delete="handleCodeDelete(index)"
                @paste="handleCodePaste"
                class="code-digit"
                autocomplete="one-time-code"
                :disabled="codeBurned"
              />
            </div>
          </div>

          <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading || !isCodeComplete || codeBurned">
            {{ isLoading ? $t('auth.verifying') : $t('auth.continue') }}
          </BaseButton>

          <!-- 32: код сожжён, ввод бесполезен до нового кода -->
          <BaseButton
            monochrome
            variant="ghost"
            v-if="codeBurned && !codeThrottled"
            type="button"
            class="btn btn-outline btn-block"
            data-test="request-new-code"
            :disabled="isResending"
            @click="requestNewCode"
          >
            {{ $t('auth.codeLimits.requestNew') }}
          </BaseButton>

          <div v-if="!codeBurned" class="resend-link">
            <a 
              href="#" 
              class="resend-code"
              @click.prevent="handleResendCode" 
              :class="{ disabled: isResending || resendCooldown > 0 || codeThrottled }"
            >
              <template v-if="isResending">{{ $t('auth.sending') }}</template>
              <template v-else-if="resendCooldown > 0">{{ $t('auth.resendCodeIn', { seconds: resendCooldown }) }}</template>
              <template v-else>{{ $t('auth.resendCode') }}</template>
            </a>
          </div>

          <div class="resend-link">
            <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.changeEmail') }}</a>
          </div>

          <div class="divider"><span>{{ $t('auth.or') }}</span></div>

          <BaseButton
            monochrome
            variant="ghost"
            type="button"
            class="btn btn-outline btn-block"
            @click="switchToPassword">
            {{ $t('auth.continueWithPassword') }}
          </BaseButton>
        </form>
      </template>

      <!-- Step 2b: Password Login (existing user chose password) -->
      <template v-else-if="step === 'password'">
        <h2 class="modal-title">{{ $t('auth.login') }}</h2>

        <form @submit.prevent="handlePasswordSubmit" class="auth-form">
          <!-- Hidden email for browser password autosave -->
          <input 
            type="email" 
            :value="form.email" 
            autocomplete="username" 
            class="sr-only" 
            aria-hidden="true"
            tabindex="-1"
          />
          <div class="form-group">
            <label class="form-label">{{ $t('auth.emailAddress') }}</label>
            <div class="email-display">
              <span>{{ form.email }}</span>
              <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.edit') }}</a>
            </div>
          </div>

          <!-- Migration Notice -->
          <div v-if="migrationDomain" class="migration-notice">
            <i class="zmdi zmdi-info-outline" />
            <span>{{ $t('auth.accountMigrated', { domain: migrationDomain }) }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.password') }}</label>
            <div class="password-input-wrapper">
              <BaseInput bare
                ref="passwordInput"
                v-model="form.password" 
                :type="showPassword ? 'text' : 'password'" 
                class="form-input" 
                :placeholder="$t('auth.enterPassword')"
                autocomplete="current-password"
                required
              />
              <button type="button"
                class="toggle-password"
                tabindex="-1"
                @click="showPassword = !showPassword">
                <i :class="showPassword ? 'zmdi zmdi-eye-off' : 'zmdi zmdi-eye'" />
              </button>
            </div>
            <div class="forgot-password">
              <a href="#" @click.prevent="switchToRecovery">{{ $t('auth.forgotPassword') }}</a>
            </div>
          </div>

          <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading">
            {{ isLoading ? $t('auth.entering') : $t('auth.login') }}
          </BaseButton>

          <div class="divider"><span>{{ $t('auth.or') }}</span></div>

          <BaseButton
            monochrome
            variant="ghost"
            type="button"
class="btn btn-outline btn-block"
@click="switchToCode"
:disabled="isSendingCode">
            {{ isSendingCode ? $t('auth.sending') : $t('auth.loginByCode') }}
          </BaseButton>
        </form>
      </template>

      <!-- Step: Email Not Found Confirmation -->
      <template v-else-if="step === 'email-not-found'">
        <div class="email-not-found-icon">
          <i class="zmdi zmdi-email" />
        </div>
        <h2 class="modal-title">{{ $t('auth.emailNotFound') }}</h2>
        
        <div class="email-not-found-content">
          <p class="modal-subtitle">
            {{ $t('auth.emailNotFoundMessage') }}
          </p>
          <p class="email-highlight">{{ form.email }}</p>
          <p class="modal-subtitle">
            {{ $t('auth.createAccountPrompt') }}
          </p>
        </div>

        <div class="auth-form">
          <p class="modal-subtitle">{{ $t('emailLink.telegramLogin') }}</p>
          <SocialAuthButtons
            mode="login"
            :light="light"
            :inviter-login="form.inviterLogin"
            @totp-required="handleTelegramTotp"
          />
          <BaseButton monochrome
            type="button" 
            class="btn btn-primary btn-block" 
            @click="proceedToRegistration"
          >
            {{ $t('auth.createAccount') }}
          </BaseButton>

          <BaseButton
            monochrome
            variant="ghost"
            type="button" 
            class="btn btn-outline btn-block mt-12" 
            @click="goBackToEmail"
          >
            {{ $t('auth.tryAnotherEmail') }}
          </BaseButton>
        </div>
      </template>

      <!-- Step: Inviter (new user registration) -->
      <template v-else-if="step === 'inviter'">
        <h2 class="modal-title">{{ $t('auth.inviterLogin') }}</h2>
        
        <p class="modal-subtitle">
          {{ $t('auth.enterInviterHint') || 'Enter the login of your referrer to continue registration' }}
        </p>

        <form @submit.prevent="handleInviterSubmit" class="auth-form">
          <div class="form-group">
            <label class="form-label">{{ $t('auth.emailAddress') }}</label>
            <div class="email-display">
              <span>{{ form.email }}</span>
              <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.edit') }}</a>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.inviterLogin') }}</label>
            <BaseInput bare
              v-model="form.inviterLogin" 
              type="text" 
              class="form-input" 
              :placeholder="$t('auth.enterInviterLogin')"
              required
            />
          </div>

          <div v-if="errorMessage" class="alert alert-error">
            <i class="zmdi zmdi-alert-circle" />
            <span>{{ errorMessage }}</span>
          </div>

          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading || !form.inviterLogin">
            {{ isLoading ? $t('auth.loading') : $t('auth.continue') }}
          </BaseButton>

          <div class="divider"><span>{{ $t('auth.or') }}</span></div>

          <SocialAuthButtons
            mode="register"
            :light="light"
            :inviter-login="form.inviterLogin"
            @totp-required="handleTelegramTotp"
          />
        </form>
      </template>

      <!-- Step 2c: Registration (new user) -->
      <template v-else-if="step === 'register'">
        <h2 class="modal-title">{{ $t('auth.createPassword') }}</h2>
        
        <p class="modal-subtitle">
          {{ $t('auth.createPasswordSubtitle') }}
        </p>

        <form @submit.prevent="handleRegisterSubmit" class="auth-form">
          <div v-if="embedded" class="form-group">
            <label class="form-label" for="entry-name">{{ $t('entry.name') }}</label>
            <BaseInput bare
              id="entry-name"
              v-model="form.firstName"
              type="text"
              class="form-input"
              autocomplete="given-name"
              maxlength="80"
              required
            />
          </div>
          <!-- Hidden email for browser password autosave -->
          <input 
            type="email" 
            :value="form.email" 
            autocomplete="username" 
            class="sr-only" 
            aria-hidden="true"
            tabindex="-1"
          />
          <div class="form-group">
            <label class="form-label">{{ $t('auth.emailAddress') }}</label>
            <div class="email-display">
              <span>{{ form.email }}</span>
              <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.edit') }}</a>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.password') }}</label>
            <div class="password-input-wrapper">
              <BaseInput bare
                v-model="form.password" 
                :type="showPassword ? 'text' : 'password'" 
                class="form-input" 
                :placeholder="$t('auth.enterPassword')"
                autocomplete="new-password"
                required
              />
              <button type="button"
                class="toggle-password"
                tabindex="-1"
                @click="showPassword = !showPassword">
                <i :class="showPassword ? 'zmdi zmdi-eye-off' : 'zmdi zmdi-eye'" />
              </button>
            </div>
            
            <!-- Password requirements checklist -->
            <div class="password-requirements" v-if="form.password.length > 0">
              <div class="requirement" :class="{ valid: passwordValidation.minLength }">
                <i :class="passwordValidation.minLength ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.minLength') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasUppercase }">
                <i :class="passwordValidation.hasUppercase ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.uppercase') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasLowercase }">
                <i :class="passwordValidation.hasLowercase ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.lowercase') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasDigit }">
                <i :class="passwordValidation.hasDigit ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.digit') }}</span>
              </div>
              <div class="requirement" :class="{ valid: passwordValidation.hasSpecial }">
                <i :class="passwordValidation.hasSpecial ? 'zmdi zmdi-check-circle' : 'zmdi zmdi-circle-o'" />
                <span>{{ $t('auth.passwordRequirements.special') }}</span>
              </div>
            </div>
          </div>

          <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading || !isPasswordValid || (embedded && !form.firstName.trim())">
            {{ isLoading ? $t('auth.creating') : $t('auth.continue') }}
          </BaseButton>
        </form>

        <div v-if="!embedded" class="divider"><span>{{ $t('auth.or') }}</span></div>

        <p class="modal-subtitle">{{ $t('emailLink.telegramLogin') }}</p>
        <SocialAuthButtons
          mode="login"
          :light="light"
          :inviter-login="form.inviterLogin"
          @totp-required="handleTelegramTotp"
        />

        <div class="auth-footer auth-footer--links">
          <a href="/app/law/terms" target="_blank">{{ $t('auth.termsOfUse') }}</a>
          <span class="separator">|</span>
          <a href="/app/law/privacy" target="_blank">{{ $t('auth.privacyPolicy') }}</a>
        </div>
      </template>

      <!-- Step 3: Login Approval (2FA) - kept from original -->
      <template v-else-if="step === 'approval'">
        <form @submit.prevent="handleApprovalSubmit" class="auth-form approval-form">
          <div class="approval-info">
            <h3>{{ $t('auth.loginApproval') }}</h3>
            <p v-if="approvalType === 'totp'">{{ $t('auth.enterTotpCode') }}</p>
            <p v-else>{{ $t('auth.approvalCodeSent', { method: approvalMethodLabel }) }}</p>
            <p class="approval-session" v-if="approvalSessionNumber">
              {{ $t('auth.sessionNumber') }}: {{ approvalSessionNumber }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.approvalConfirmationCode') }}</label>
            <div class="code-input-wrapper">
              <input
                v-for="(_, index) in approvalCodeLength"
                :key="index"
                ref="codeInputs"
                type="text"
                inputmode="numeric"
                maxlength="1"
                v-model="codeDigits[index]"
                @input="handleCodeInput($event, index)"
                @keydown.delete="handleCodeDelete(index)"
                @paste="handleCodePaste"
                class="code-digit"
                autocomplete="one-time-code"
                :disabled="codeBurned"
              />
            </div>
          </div>

          <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

          <!-- 32: доставленный код сожжён — присылаем новый в тот же канал -->
          <BaseButton
            monochrome
            variant="ghost"
            v-if="codeBurned && !codeThrottled && approvalType !== 'totp'"
            type="button"
            class="btn btn-outline btn-block"
            data-test="request-new-code"
            :disabled="isLoading"
            @click="requestNewCode"
          >
            {{ $t('auth.codeLimits.requestNew') }}
          </BaseButton>

          <div class="approval-actions">
            <BaseButton
              monochrome
              variant="ghost"
              type="button"
              class="btn btn-outline"
              @click="cancelApproval"
              :disabled="isLoading"
            >
              {{ $t('auth.back') }}
            </BaseButton>
            <BaseButton monochrome
              type="submit"
              class="btn btn-primary"
              :disabled="isLoading || !isCodeComplete || codeBurned"
            >
              {{ isLoading ? $t('auth.verifying') : $t('auth.confirm') }}
            </BaseButton>
          </div>
        </form>
      </template>

      <!-- Footer for email step only -->
      <div v-if="step === 'email' && !embedded" class="auth-footer">
        <p v-if="mode !== 'register'">{{ $t('auth.noAccount') }} <a href="#" @click.prevent="switchToRegister">{{ $t('auth.register') }}</a></p>
        <p v-else>{{ $t('auth.hasAccount') }} <a href="#" @click.prevent="switchToLogin">{{ $t('auth.login') }}</a></p>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import BaseButton from '@/shared/components/base/BaseButton.vue'
import BaseInput from '@/shared/components/base/BaseInput.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useModalOverlay } from '@/composables/useModalOverlay'
import { ProfileApi } from '@/modules/profile/api/profileApi'
import type { OtpChoice, OtpChoiceChannel } from '@/modules/profile/api/profileApi'
import { getErrorMessage } from '@/shared/utils/apiErrorParser'
import { AuthRequestCancelled, assertCurrentAuthRequest } from '@/shared/api/authRequestGuard'
import { httpClient } from '@/shared/api/httpClient'
import { isCaptchaSessionExpiredError, isEmailAlreadyExistsError, isLoginFailError, requestCaptchaToken } from '@/shared/utils/captchaRetryService'
import { getCaptchaFieldName, isGeetestProvider } from '@/shared/utils/captchaConfig'
import CaptchaWidget from '@/shared/components/CaptchaWidget.vue'
import SocialAuthButtons from './SocialAuthButtons.vue'
import type { TelegramTotpSignal } from '../services/TelegramLoginPollingService'
import { useCodeLimit } from '@/composables/useCodeLimit'
import EmailDomainSuggestions from '@/shared/components/EmailDomainSuggestions.vue'
import OtpChannelChoice from '@/modules/auth/components/OtpChannelChoice.vue'
import TelegramDeliveryRecovery from './TelegramDeliveryRecovery.vue'
import { isTelegramDeliveryFailure, parseTelegramRecovery } from '../utils/telegramDeliveryRecovery'
import type { TelegramRecovery } from '../utils/telegramDeliveryRecovery'

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  embedded?: boolean
  inline?: boolean
  teleportTo?: HTMLElement
  light?: boolean
  mode?: 'login' | 'register'
  registrationPopup?: boolean
  initialEmail?: string
  autoSendCode?: boolean
  /** OTP-R6c: pending-вход через Telegram из окна регистрации — открыть сразу шаг TOTP. */
  telegramTotp?: TelegramTotpSignal | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'switch-to-register', email?: string): void
  (e: 'switch-to-login'): void
  (e: 'switch-to-recovery', email?: string): void
  (e: 'step-change', step: Step): void
}>()

type Step = 'email' | 'email-not-found' | 'code' | 'password' | 'register' | 'approval' | 'inviter' | 'choice' | 'delivery-recovery'

const profileStore = useProfileStore()
const step = ref<Step>('email')
const modalContent = ref<HTMLElement>()
const focus = () => modalContent.value?.querySelector<HTMLInputElement>('input[type="email"]')?.focus({ preventScroll: true })
defineExpose({ focus })

function handleDialogKeydown(event: KeyboardEvent) {
  if (props.embedded || props.inline) return
  if (event.key === 'Escape') {
    event.stopPropagation()
    close()
    return
  }
  if (event.key !== 'Tab' || !modalContent.value) return
  const elements = [...modalContent.value.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), [tabindex="0"]')]
    .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0)
  const first = elements[0]
  const last = elements[elements.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first?.focus()
  }
}
watch(step, value => emit('step-change', value), { immediate: true, flush: 'sync' })
// Лимиты кодов: 32 — код сожжён (поле закрыто до нового), 33 — повтор закрыт на 15 минут.
const { isBurned: codeBurned, isThrottled: codeThrottled, apply: applyCodeLimit, reset: resetCodeLimit } = useCodeLimit()
let generation = 0
// OTP-R3: реактивный признак нужен store — по нему он синхронно снимает
// busy отменённой попытки, не дожидаясь её старого ответа.
const passwordRequestActive = ref(false)
const approvalRequestActive = ref(false)
const registrationRequestActive = ref(false)
// Pending bearer незавершённого входа принадлежит своей попытке: при отказе от неё
// удаляем только его и только если в хранилище всё ещё он, не чужой/полный токен.
let pendingApproval: { token: string | null } | null = null
const telegramRecovery = ref<TelegramRecovery | null>(null)
const invalidateRequests = () => {
  generation++
  telegramRecovery.value = null
  passwordRequestActive.value = false
  approvalRequestActive.value = false
  registrationRequestActive.value = false
  const pending = pendingApproval
  pendingApproval = null
  if (pending?.token && httpClient.getToken() === pending.token) httpClient.clearToken()
  // Сожжённый код принадлежит брошенной попытке; окно 33 — серверное, оно остаётся.
  if (codeBurned.value) resetCodeLimit()
  pendingCaptchaAction.value = null
  isLoading.value = false
  isResending.value = false
  isSendingCode.value = false
  if (redirectTimer !== null) clearTimeout(redirectTimer)
  redirectTimer = null
  isRedirecting.value = false
}
const isLoading = ref(false)
const isResending = ref(false)
const isSendingCode = ref(false)
const isRedirecting = ref(false)
const errorMessage = ref('')
const showPassword = ref(false)
const migrationDomain = ref<string | null>(null)
const sessionNumber = ref('')
const isEmailFocused = ref(false)

const handleEmailSuggestionSelect = (email: string) => {
  form.email = email
  // Note: suggestions will hide automatically because selected email has a complete domain
}

const handleEmailBlur = () => {
  // Small delay to allow click on suggestion to process before hiding
  setTimeout(() => {
    isEmailFocused.value = false
  }, 150)
}

// Resend cooldown timer
const RESEND_COOLDOWN_SECONDS = 60
const resendCooldown = ref(0)
let cooldownIntervalId: ReturnType<typeof setInterval> | null = null

const startResendCooldown = () => {
  resendCooldown.value = RESEND_COOLDOWN_SECONDS
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
  }
  cooldownIntervalId = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      if (cooldownIntervalId) {
        clearInterval(cooldownIntervalId)
        cooldownIntervalId = null
      }
    }
  }, 1000)
}

onBeforeUnmount(() => {
  // Принятый redirect текущей попытки не отменяем: хост (AppHeader -> dashboard) мог снять
  // окно раньше таймера, и sso_redirect потерялся бы. Устаревший таймер уже снят инвалидацией.
  if (redirectTimer !== null) window.location.assign(redirectUrl)
  invalidateRequests()
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
  }
})

// Show redirect loading overlay and perform redirect
let redirectTimer: ReturnType<typeof setTimeout> | null = null
let redirectUrl = ''
const redirectWithLoading = (url: string) => {
  const attempt = generation
  if (redirectTimer !== null) clearTimeout(redirectTimer)
  redirectUrl = url
  isRedirecting.value = true
  // Small delay to ensure overlay is visible before navigation
  redirectTimer = setTimeout(() => {
    redirectTimer = null
    if (attempt !== generation || !props.modelValue) return
    window.location.assign(url)
  }, 100)
}

// reCAPTCHA state
const captchaRef = ref<InstanceType<typeof CaptchaWidget> | null>(null)
const captchaToken = ref('')
const captchaSession = ref('')
const captchaSessionExpiry = ref(0)

// Track which action triggered captcha (for routing after verification)
type CaptchaAction = 'email' | 'login' | 'register' | 'send_code' | null
const pendingCaptchaAction = ref<CaptchaAction>(null)

const onCaptchaVerified = (token: string) => {
  captchaToken.value = token
  const action = pendingCaptchaAction.value
  pendingCaptchaAction.value = null // reset after use
  
  switch (action) {
    case 'email':
      performEmailCheck(token)
      break
    case 'login':
      performPasswordLogin(token)
      break
    case 'register':
      performRegister(token)
      break
    case 'send_code':
      performSendCode(token)
      break
    default:
      // No action pending, just store the token for later use if needed
      console.log('[GonkaGame] Captcha verified silently, token stored')
  }
}

const onCaptchaExpired = () => {
  captchaToken.value = ''
}

const onCaptchaCancelled = () => {
  captchaToken.value = ''
  pendingCaptchaAction.value = null
}

const onCaptchaError = () => {
  onCaptchaCancelled()
  errorMessage.value = t('auth.captchaError')
}

// Watch for auto-send code trigger from RegistrationModal
watch(
  () => props.modelValue,
  async (isOpen) => {
    if (!isOpen) resetState()
    if (isOpen && props.initialEmail) form.email = props.initialEmail
    if (isOpen && !props.embedded) nextTick(focus)
    if (isOpen && props.autoSendCode && props.initialEmail) {
      // Pre-fill email and auto-send code
      form.email = props.initialEmail
      const attempt = generation
      isLoading.value = true
      errorMessage.value = ''
      try {
        const result = await ProfileApi.sendLoginCode(form.email, {}, () => attempt === generation)
        if (attempt !== generation) return
        // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
        if (takeChoice(result, 'code')) return
        sessionNumber.value = result.session || ''
        codeReceiver.value = result.reciever || 'email'
        receiverAddress.value = result.reciever_address || form.email
        step.value = 'code'
        codeDigits.fill('')
        startResendCooldown() // Start 60s cooldown after showing the form
        nextTick(() => codeInputs.value[0]?.focus())
      } catch (error: any) {
        if (attempt !== generation) return
        if (takeTelegramFailure(error)) return
        errorMessage.value = getErrorMessage(error)
        step.value = 'email'
      } finally {
        if (attempt === generation) isLoading.value = false
      }
    }
  }
)
const codeReceiver = ref<'email' | 'telegram'>('email')
const receiverAddress = ref('')

// Спринт 14: бэкенд вернул фазу выбора — код не отправлен, ждём ответа.
const choiceChannels = ref<string[]>([])
// Какой поток ждёт ответа: от него зависит, куда уйдёт повторный запрос.
const choiceFor = ref<'code' | 'approval' | null>(null)
// Каналы прошлого выбора: по ним решаем, показывать ли «отправить иначе».
const lastChoiceChannels = ref<string[]>([])
// Ответ действует только внутри текущего входа, включая resend и повтор капчи.
const chosenChannel = ref<OtpChoiceChannel>()
const canSwitchChannel = computed(() => lastChoiceChannels.value.length > 1)

const takeTelegramFailure = (error: unknown): boolean => {
  if (!isTelegramDeliveryFailure(error)) return false
  telegramRecovery.value = parseTelegramRecovery(error)
  captchaSession.value = telegramRecovery.value?.captchaSession ?? ''
  captchaToken.value = ''
  pendingCaptchaAction.value = null
  choiceChannels.value = []
  lastChoiceChannels.value = []
  choiceFor.value = null
  sessionNumber.value = ''
  codeDigits.fill('')
  errorMessage.value = ''
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
  step.value = 'delivery-recovery'
  return true
}

const retryTelegramDelivery = async (channel: OtpChoiceChannel) => {
  if (!props.modelValue || isLoading.value || isResending.value || codeThrottled.value
    || pendingCaptchaAction.value === 'send_code'
    || step.value !== 'delivery-recovery' || !telegramRecovery.value?.channels.includes(channel)) return
  chosenChannel.value = channel
  await performSendCode(null, true, channel)
}

/**
 * Спринт 14: фазу выбора надо поймать ДО перехода на экран ввода кода —
 * иначе экран попросит код, которого не существует.
 */
const takeChoice = (
  result: { choose?: OtpChoice },
  flow: 'code' | 'approval'
): boolean => {
  if (!result.choose) return false

  choiceChannels.value = result.choose.channels
  // Путь назад к выбору есть только на экране ввода кода — там и помним.
  if (flow === 'code') lastChoiceChannels.value = result.choose.channels
  choiceFor.value = flow
  // Старую одноразовую сессию повторно использовать нельзя.
  if (flow === 'code') captchaSession.value = result.choose.captcha_session ?? ''
  step.value = 'choice'

  return true
}

/** Человек выбрал канал — повторяем тот же запрос, теперь с ответом. */
const handleChoicePick = async (channel: OtpChoiceChannel): Promise<void> => {
  if (isLoading.value || !choiceChannels.value.includes(channel)) return
  const flow = choiceFor.value
  chosenChannel.value = channel

  if (flow === 'code') await performSendCode(null, true, channel)
  else if (flow === 'approval') await performSendApprovalCode(channel)
}

/** Вернуться к выбору с экрана ввода кода. */
const switchChannel = () => {
  if (isLoading.value) return
  errorMessage.value = ''
  choiceChannels.value = lastChoiceChannels.value
  choiceFor.value = 'code'
  step.value = 'choice'
}

const form = reactive({
  email: '',
  password: '',
  firstName: '',
  inviterLogin: ''
})

// Referral info for new user registration
const referralId = ref<number | null>(null)

// Code input handling (7 digits for delivered approval, 6 for TOTP and login code)
const codeDigits = reactive<string[]>(new Array(7).fill(''))
const codeInputs = ref<HTMLInputElement[]>([])
const passwordInput = ref<HTMLInputElement | null>(null)
// OTP-R6c: длину задаёт тип подтверждения — TOTP из приложения 6 цифр, доставленный код 7.
const approvalCodeLength = computed(() => approvalType.value === 'totp' ? 6 : 7)
const codeLength = computed(() => step.value === 'approval' ? approvalCodeLength.value : 6)
// Keep readiness independent of template refs updated during rendering.
const isCodeComplete = computed(() => {
  if (step.value !== 'code' && step.value !== 'approval') return false
  return codeDigits.slice(0, codeLength.value).every(d => d.length === 1)
})
const getCodeValue = () => codeDigits.slice(0, codeLength.value).join('')

// Password validation for registration
const passwordValidation = computed(() => {
  const pwd = form.password
  return {
    minLength: pwd.length >= 8,
    hasUppercase: /[A-Z]/.test(pwd),
    hasLowercase: /[a-z]/.test(pwd),
    hasDigit: /[0-9]/.test(pwd),
    hasSpecial: /[^A-Za-z0-9]/.test(pwd),
  }
})

const isPasswordValid = computed(() => {
  const v = passwordValidation.value
  return v.minLength && v.hasUppercase && v.hasLowercase && v.hasDigit && v.hasSpecial
})

// 2FA Approval state (kept from original)
const approvalCode = ref('')
const approvalMethods = ref<string[]>([])
const approvalType = ref<'otp' | 'totp'>('otp') // OTP = email/telegram, TOTP = Google Authenticator
const approvalRedirect = ref('/')
const approvalReferer = ref('')
const approvalSessionNumber = ref('')

const methodLabelMap: Record<string, string> = {
  email: 'E-mail',
  telegram: 'Telegram',
}

const approvalMethodLabel = computed(() => {
  if (approvalMethods.value.length === 0) return t('auth.defaultApprovalChannel')
  return approvalMethods.value
    .map((method) => methodLabelMap[method] ?? method)
    .join(t('auth.approvalMethodsJoin'))
})

const close = () => {
  emit('update:modelValue', false)
  resetState()
}

// Modal overlay click handling (prevents accidental close on text selection)
const { handleOverlayMouseDown, handleOverlayMouseUp } = useModalOverlay(close)

const resetState = () => {
  invalidateRequests()
  step.value = 'email'
  errorMessage.value = ''
  form.email = ''
  form.password = ''
  form.firstName = ''
  form.inviterLogin = ''
  referralId.value = null
  migrationDomain.value = null
  sessionNumber.value = ''
  codeReceiver.value = 'email'
  receiverAddress.value = ''
  choiceChannels.value = []
  choiceFor.value = null
  lastChoiceChannels.value = []
  chosenChannel.value = undefined
  captchaSession.value = ''
  captchaToken.value = ''
  pendingCaptchaAction.value = null
  codeDigits.fill('')
  approvalCode.value = ''
  approvalMethods.value = []
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
}

const goBackToEmail = () => {
  invalidateRequests()
  if (props.embedded) { form.password = ''; form.firstName = '' }
  if (captchaToken.value || captchaSession.value || pendingCaptchaAction.value) captchaRef.value?.reset()
  captchaSession.value = ''
  captchaSessionExpiry.value = 0
  captchaToken.value = ''
  pendingCaptchaAction.value = null
  choiceChannels.value = []
  choiceFor.value = null
  lastChoiceChannels.value = []
  chosenChannel.value = undefined
  step.value = 'email'
  errorMessage.value = ''
  form.inviterLogin = ''
  referralId.value = null
  migrationDomain.value = null
  sessionNumber.value = ''
  codeDigits.fill('')
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
}

watch(() => form.email, goBackToEmail, { flush: 'sync' })

const switchToPassword = () => {
  invalidateRequests()
  step.value = 'password'
  errorMessage.value = ''
  nextTick(() => passwordInput.value?.focus())
}

const switchToCode = async () => {
  invalidateRequests()
  const attempt = generation
  errorMessage.value = ''
  isSendingCode.value = true
  try {
    await sendLoginCode()
  } finally {
    if (attempt === generation) isSendingCode.value = false
  }
}

const switchToRecovery = () => {
  const email = form.email
  close()
  emit('switch-to-recovery', email)
}

const switchToRegister = () => {
  const email = form.email
  close()
  emit('switch-to-register', email)
}
const switchToLogin = () => {
  close()
  emit('switch-to-login')
}

const proceedToRegistration = () => {
  if (props.registrationPopup) {
    switchToRegister()
    return
  }
  step.value = 'inviter'
}

const cancelApproval = () => {
  invalidateRequests()
  step.value = 'password'
  approvalCode.value = ''
  approvalMethods.value = []
  codeDigits.fill('')
  choiceChannels.value = []
  choiceFor.value = null
  errorMessage.value = ''
}

// Step 1: Validate email and trigger captcha
const handleEmailSubmit = async () => {
  if (!form.email || isLoading.value) return

  // Gmail emails are now handled via standard email flow (Google redirect disabled)

  errorMessage.value = ''

  // Basic email validation
  if (!form.email.includes('@')) {
    errorMessage.value = t('auth.enterEmail')
    return
  }

  // Set pending action and trigger Geetest popup (bind mode)
  pendingCaptchaAction.value = 'email'
  captchaRef.value?.showCaptcha?.()
}

// Called after captcha verification - performs actual email check
const performEmailCheck = async (token: string) => {
  const attempt = generation
  isLoading.value = true
  errorMessage.value = ''

  try {
    const email = form.email
    const result = await ProfileApi.checkEmail(email, token, () => attempt === generation)
    if (attempt !== generation) return
    
    // Store captcha session for subsequent requests (2 min TTL)
    captchaSession.value = result.captchaSession
    captchaSessionExpiry.value = Date.now() + 2 * 60 * 1000 // 2 minutes
    
    if (!result.exists) {
      if (props.embedded) {
        const cookieReferral = document.cookie.match(/(?:^|;\s*)referal=([^;]*)/)?.[1]
        form.inviterLogin = localStorage.getItem('partner_referral_code') || (cookieReferral ? decodeURIComponent(cookieReferral) : '')
        if (form.inviterLogin) {
          step.value = 'inviter'
          await handleInviterSubmit()
        } else {
          step.value = 'register'
        }
        return
      }
      // User not found - show confirmation dialog before registration
      step.value = 'email-not-found'
      return
    } else {
      // Existing user
      migrationDomain.value = result.domain
      
      if (result.isMigrated) {
        // Migrated user (regdate == null) -> send code and show code form
        await sendLoginCode()
      } else {
        // Regular user (has regdate) -> show password form
        step.value = 'password'
        nextTick(() => passwordInput.value?.focus())
      }
    }
  } catch (error: any) {
    if (attempt !== generation) return
    errorMessage.value = getErrorMessage(error)
  } finally {
    if (attempt === generation) isLoading.value = false
  }
}

// Helper to perform the actual send code request with step transition
const performSendCode = async (token: string | null, useSession = false, channel = chosenChannel.value) => {
  const attempt = generation
  const email = form.email
  const isCurrent = () => attempt === generation
  isLoading.value = true
  isResending.value = true
  errorMessage.value = ''

  try {
    const payload: Record<string, any> = {}
    if (useSession && captchaSession.value) {
      payload.captchaSession = captchaSession.value
      captchaSession.value = ''
    } else {
      payload.recaptchaToken = token || await requestCaptchaToken()
    }
    if (attempt !== generation) return
    if (channel) {
      payload.channel = channel
    }

    const result = await ProfileApi.sendLoginCode(email, payload, isCurrent)
    if (attempt !== generation) return

    telegramRecovery.value = null
    // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
    if (takeChoice(result, 'code')) return

    choiceChannels.value = []
    choiceFor.value = null
    // Update state from response
    sessionNumber.value = result.session || ''
    codeReceiver.value = result.reciever || 'email'
    receiverAddress.value = result.reciever_address || form.email
    
    // Transition to code step
    step.value = 'code'
    codeDigits.fill('')
    resetCodeLimit()
    startResendCooldown() // Start 60s cooldown after showing the form
    nextTick(() => codeInputs.value[0]?.focus())
  } catch (error: any) {
    if (attempt !== generation) return
    if (takeTelegramFailure(error)) return
    if (isCaptchaSessionExpiredError(error)) {
      pendingCaptchaAction.value = 'send_code'
      await handleCaptchaExpiry()
    } else {
      errorMessage.value = applyCodeLimit(error) ?? getErrorMessage(error)
    }
  } finally {
    if (attempt === generation) {
      isLoading.value = false
      isResending.value = false
    }
  }
}

// Send login code (from password step or switchToCode)
const sendLoginCode = async () => {
  await performSendCode(null, true)
}

// Resend code (from code step)
const handleResendCode = async () => {
  if (isResending.value || resendCooldown.value > 0 || codeThrottled.value) return
  await performSendCode(null, true)
}

/** 32: сожжённый код не оживить — новый код без ожидания обычной паузы. */
const requestNewCode = async () => {
  if (codeThrottled.value) return
  if (step.value === 'approval') {
    const channel = approvalMethods.value[0]
    if (channel === 'email' || channel === 'telegram') await performSendApprovalCode(channel)
    return
  }
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
  await handleResendCode()
}

// Code input handlers
const handleCodeInput = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement
  const val = input.value

  if (!/^\d*$/.test(val)) {
    codeDigits[index] = ''
    return
  }

  // Use actual number of input fields instead of hardcoded 5
  const maxIndex = codeInputs.value.length - 1
  if (val.length === 1 && index < maxIndex) {
    codeInputs.value[index + 1]?.focus()
  } else if (val.length > 1) {
    codeDigits[index] = val.slice(-1)
  }
}

const handleCodeDelete = (index: number) => {
  if (codeDigits[index]) {
    codeDigits[index] = ''
    return
  }
  if (index > 0) {
    codeDigits[index - 1] = ''
    codeInputs.value[index - 1]?.focus()
  }
}

const handleCodePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  event.stopPropagation()
  if (!props.modelValue || isLoading.value || (step.value !== 'code' && step.value !== 'approval')) return
  const paste = event.clipboardData?.getData('text')
  if (!paste) return
  const attempt = generation
  const originatingStep = step.value
  
  const numbers = paste.match(/\d/g)
  if (numbers) {
    // Use actual number of input fields instead of hardcoded 6
    const fieldsCount = codeInputs.value.length
    numbers.slice(0, fieldsCount).forEach((num, i) => {
      codeDigits[i] = num
    })
    codeInputs.value[Math.min(numbers.length, fieldsCount - 1)]?.focus()
    
    // Auto-submit if all fields are filled after paste
    if (numbers.length >= fieldsCount) {
      nextTick(() => {
        if (attempt !== generation || originatingStep !== step.value) return
        if (step.value === 'code') {
          handleCodeSubmit()
        } else if (step.value === 'approval') {
          handleApprovalSubmit()
        }
      })
    }
  }
}

// Step 2a: Verify code
const handleCodeSubmit = async () => {
  if (!props.modelValue || step.value !== 'code' || isLoading.value || !isCodeComplete.value || codeBurned.value) return

  const attempt = generation
  const email = form.email
  const code = getCodeValue()
  const pending = { token: null as string | null }
  pendingApproval = pending
  const isCurrent = () => attempt === generation && pendingApproval === pending
  isLoading.value = true
  errorMessage.value = ''

  try {
    const result = await ProfileApi.verifyLoginCode(email, code, isCurrent, (token) => {
      if (isCurrent()) pending.token = token
    })
    assertCurrentAuthRequest(isCurrent)
    
    // OTP-R6c: код — только первый фактор. Pending-токен не сохраняем как вход и не уходим со страницы.
    if (result.requires_approval) {
      if (!pending.token && result.token) {
        pending.token = result.token
        httpClient.setToken(result.token)
      }
      enterApproval(result)
      return
    }

    // Save token and redirect
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
    localStorage.setItem(storageKey, result.token)
    
    redirectWithLoading(result.location || '/dashboard')
  } catch (error: any) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    const limitMessage = applyCodeLimit(error)
    if (codeBurned.value) codeDigits.fill('')
    errorMessage.value = limitMessage ?? getErrorMessage(error)
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

// Step 2b: Password login
// Helper to perform the actual login request
const performPasswordLogin = async (token: string | null, useSession = false) => {
  const attempt = generation
  const pending = { token: null as string | null }
  pendingApproval = pending
  isLoading.value = true
  errorMessage.value = ''

  try {
    const loginPayload: Record<string, any> = {
      email: form.email,
      password: form.password
    }

    if (useSession && captchaSession.value) {
      loginPayload['captcha-session'] = captchaSession.value
    } else {
      loginPayload[getCaptchaFieldName()] = token || await requestCaptchaToken()
    }
    if (attempt !== generation) return

    passwordRequestActive.value = true
    const isCurrent = () => passwordRequestActive.value && attempt === generation
    const loginResult = await profileStore.login(loginPayload, isCurrent, (token) => {
      if (isCurrent() && pendingApproval === pending) pending.token = token
    })
    if (attempt !== generation) return

    // Handle new JSON response format with requires_approval
    if (loginResult.requires_approval || loginResult.choose) {
      enterApproval(loginResult)
      return
    }

    // Legacy: check URL-based approval flow (for backward compatibility)
    if (loginResult.location?.includes('login-approval')) {
      activateApprovalFlow(loginResult.location)
      return
    }

    // SSO redirect handling
    if (loginResult.sso_redirect) {
      redirectWithLoading(loginResult.sso_redirect)
      return
    }

    redirectWithLoading(loginResult.location || '/')
  } catch (error: any) {
    if (attempt !== generation || error instanceof AuthRequestCancelled) return
    if (isCaptchaSessionExpiredError(error)) {
      await handleCaptchaExpiry()
    } else if (isLoginFailError(error)) {
      errorMessage.value = t('auth.loginFailWrongCredentials')
    } else {
      errorMessage.value = getErrorMessage(error)
    }
  } finally {
    if (attempt === generation) isLoading.value = false
  }
}

/** Спринт 14: /profile/login при двух каналах код не шлёт — шлём отсюда. */
const performSendApprovalCode = async (channel: OtpChoiceChannel): Promise<void> => {
  const attempt = generation
  const pending = pendingApproval
  const isCurrent = () => attempt === generation && props.modelValue && pending === pendingApproval
  isLoading.value = true
  errorMessage.value = ''
  try {
    const result = await ProfileApi.sendLoginApprovalCode(channel, isCurrent, (token) => {
      if (isCurrent() && pending) pending.token = token
    })
    assertCurrentAuthRequest(isCurrent)
    // Ровно один канал: тот, куда код действительно ушёл.
    approvalMethods.value = [result.reciever]
    choiceChannels.value = []
    choiceFor.value = null
    codeDigits.fill('')
    resetCodeLimit()
    step.value = 'approval'
  } catch (error: unknown) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    errorMessage.value = applyCodeLimit(error) ?? getErrorMessage(error)
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

// Global handler for captcha expiry to avoid redundancy
const handleCaptchaExpiry = async () => {
  const attempt = generation
  // Geetest: дёргаем локальный виджет, чтобы не показывать лишний попап.
  // Проверка captchaRef обязательна — без неё вызов молча уходил в никуда,
  // и пользователь оставался с упавшим входом и пустым экраном.
  if (isGeetestProvider() && captchaRef.value) {
    captchaRef.value.reset()
    captchaRef.value.showCaptcha()
    return
  }

  // Fallback to the generic popup for reCAPTCHA/Turnstile
  try {
    const freshToken = await requestCaptchaToken()
    if (attempt !== generation) return
    // Dispatch based on pending action
    const action = pendingCaptchaAction.value
    switch (action) {
      case 'login':
        performPasswordLogin(freshToken)
        break
      case 'register':
        performRegister(freshToken)
        break
      case 'send_code':
        pendingCaptchaAction.value = null
        await performSendCode(freshToken)
        break
      default:
        performPasswordLogin(freshToken)
    }
  } catch {
    if (attempt !== generation) return
    // Отмена попапа или его сбой. Раньше здесь был пустой catch: запрос уже
    // упал с 11011, а на экране не появлялось ничего — вход выглядел
    // сломанным. Теперь человек хотя бы видит, что произошло.
    pendingCaptchaAction.value = null
    errorMessage.value = t('auth.captchaExpired')
  }
}

const handlePasswordSubmit = async () => {
  if (!form.password || isLoading.value) return
  // Set pending action for captcha expiry handling
  pendingCaptchaAction.value = 'login'
  // Try login using the session from email step first
  await performPasswordLogin(null, true)
}

// Handle inviter submission - validate sponsor and proceed to registration
const handleInviterSubmit = async () => {
  if (!form.inviterLogin) {
    errorMessage.value = t('auth.inviterRequired')
    return
  }

  const attempt = generation
  const login = form.inviterLogin
  referralId.value = null
  isLoading.value = true
  errorMessage.value = ''

  try {
    const sponsorRes = await profileStore.searchSponsor({ login }) as { result?: string; rid?: number }
    if (attempt !== generation || login !== form.inviterLogin) return
    if (sponsorRes.result !== 'success') {
      errorMessage.value = t('auth.inviterNotFound')
      return
    }
    referralId.value = sponsorRes.rid ?? null
    
    // Proceed to registration step
    step.value = 'register'
    nextTick(() => passwordInput.value?.focus())
  } catch (error: any) {
    if (attempt !== generation || login !== form.inviterLogin) return
    errorMessage.value = t('auth.inviterNotFound')
  } finally {
    if (attempt === generation) isLoading.value = false
  }
}

// Helper to perform the actual registration request
const performRegister = async (token: string | null, useSession = false) => {
  const attempt = generation
  registrationRequestActive.value = true
  const isCurrent = () => registrationRequestActive.value && attempt === generation
  isLoading.value = true
  errorMessage.value = ''

  try {
    const registerPayload: Record<string, any> = {
      email: form.email,
      password: form.password
    }
    if (props.embedded) registerPayload.first_name = form.firstName.trim()

    if (referralId.value) {
      registerPayload.referral = referralId.value
    }

    if (useSession) {
      registerPayload['captcha-session'] = captchaSession.value || ''
    }
    if (token) {
      registerPayload[getCaptchaFieldName()] = token
    } else if (captchaToken.value) {
      registerPayload[getCaptchaFieldName()] = captchaToken.value
    }

    await profileStore.register(registerPayload, isCurrent)
    if (!isCurrent()) return
    redirectWithLoading('/dashboard')
  } catch (error: any) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    if (isEmailAlreadyExistsError(error)) {
      // Email already registered - switch to login code flow
      pendingCaptchaAction.value = 'send_code'
      await performSendCode(null, true)
      if (!isCurrent()) return
      errorMessage.value = t('auth.emailAlreadyExists')
      return
    }
    if (isCaptchaSessionExpiredError(error)) {
      pendingCaptchaAction.value = 'register'
      handleCaptchaExpiry()
    } else {
      errorMessage.value = getErrorMessage(error)
    }
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

// Step 2c: Registration (for new users after check-email)
const handleRegisterSubmit = async () => {
  if (!form.password || isLoading.value) return
  if (props.embedded && (!form.firstName.trim() || !isPasswordValid.value)) return
  pendingCaptchaAction.value = 'register'
  await performRegister(null, true)
}

/**
 * Шаг подтверждения после первого фактора (пароль или код из письма).
 * OTP-R6c: для TOTP нет доставки и выбора канала — сразу поле из 6 цифр.
 */
const enterApproval = (result: {
  approval_type?: 'otp' | 'totp'
  approval_methods?: string[]
  location?: string
  session_id?: number
  choose?: OtpChoice
}) => {
  approvalType.value = result.approval_type === 'totp' ? 'totp' : 'otp'
  approvalMethods.value = result.approval_methods || ['email']
  approvalRedirect.value = result.location || '/dashboard'
  approvalSessionNumber.value = String(result.session_id || '')
  approvalReferer.value = ''
  approvalCode.value = ''
  // Clear code digits
  codeDigits.fill('')
  // Выбор не должен терять номер сессии и адрес возврата после проверки.
  if (approvalType.value !== 'totp' && takeChoice(result, 'approval')) return
  step.value = 'approval'
}

/**
 * OTP-R6c: вход через Telegram у аккаунта с приложением-аутентификатором дал только
 * pending-токен. Он принадлежит этой попытке входа и становится полным лишь после TOTP.
 */
const handleTelegramTotp = (signal: TelegramTotpSignal) => {
  invalidateRequests()
  pendingApproval = { token: signal.token }
  httpClient.setToken(signal.token)
  errorMessage.value = ''
  enterApproval(signal)
}

// Одна передача — один шаг: повторное открытие окна не поднимает снятую сессию.
let consumedTelegramTotp: TelegramTotpSignal | null = null
watch(
  () => [props.modelValue, props.telegramTotp] as const,
  ([isOpen, signal]) => {
    if (!isOpen || !signal || signal === consumedTelegramTotp) return
    consumedTelegramTotp = signal
    handleTelegramTotp(signal)
  },
  { immediate: true }
)

// 2FA Approval flow (kept from original)
const activateApprovalFlow = (location: string) => {
  try {
    const url = new URL(location, window.location.origin)
    const methods = url.searchParams.get('amethods') ?? 'email'
    approvalMethods.value = methods.split(',').map((item) => item.trim()).filter(Boolean)
    // OTP-R6c: только приложение-аутентификатор — поле TOTP.
    approvalType.value = approvalMethods.value.length > 0 && approvalMethods.value.every((item) => item === '2fa') ? 'totp' : 'otp'
    const redirectParam = url.searchParams.get('redirect')
    const refererParam = url.searchParams.get('referer')
    approvalRedirect.value = redirectParam ?? refererParam ?? '/'
    approvalReferer.value = refererParam ?? ''
    approvalCode.value = ''
    step.value = 'approval'
  } catch (error) {
    approvalMethods.value = ['email']
    approvalType.value = 'otp'
    approvalRedirect.value = '/'
    approvalReferer.value = ''
    approvalCode.value = ''
    step.value = 'approval'
    console.warn('[GonkaGame][auth] failed to parse approval location', error)
  }
}

const handleApprovalSubmit = async () => {
  if (!props.modelValue || step.value !== 'approval' || isLoading.value || codeBurned.value) return
  const code = getCodeValue()
  if (!code || !isCodeComplete.value) {
    errorMessage.value = t('auth.enterApprovalCode')
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  const attempt = generation
  const pending = pendingApproval
  const redirect = approvalRedirect.value
  const referer = approvalReferer.value || redirect
  approvalRequestActive.value = true
  const isCurrent = () => approvalRequestActive.value && attempt === generation && pending === pendingApproval
  try {
    const approvalResult = await profileStore.confirmLoginApproval({ code, redirect, referer }, isCurrent, {
      onPendingToken: (token) => { if (isCurrent() && pending) pending.token = token },
      // Полный токен больше не pending: отмена после approval его не удаляет.
      onApproved: () => { if (isCurrent() && pending) pending.token = null },
    })
    assertCurrentAuthRequest(isCurrent)
    redirectWithLoading(approvalResult.location || redirect || '/')
  } catch (error: any) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    // R6c: error_code 31 — сервер временно не принимает коды из приложения,
    // pending-сессия жива: после паузы новый код примется.
    const limitMessage = applyCodeLimit(error)
    errorMessage.value = error?.error_code === 31 ? t('auth.totpTooManyAttempts') : limitMessage ?? getErrorMessage(error)
    // Отклонённый код бесполезен — поле готово к новому.
    if ((error?.error_code !== undefined && approvalType.value === 'totp') || codeBurned.value) codeDigits.fill('')
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

// Global keyboard handler for digit input (code and approval steps)
const handleGlobalKeydown = (event: KeyboardEvent) => {
  // Only respond when in code or approval step
  if (step.value !== 'code' && step.value !== 'approval') return
  if (!props.modelValue || isLoading.value) return
  
  // Don't intercept if user is focused on a different input outside modal
  const activeElement = document.activeElement
  const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
  const isModalElement = activeElement?.closest('.modal-content')
  
  // Allow if focused in modal or no other input is focused
  if (isInputElement && !isModalElement) return
  
  const fieldsCount = codeInputs.value.length
  if (fieldsCount === 0) return
  const attempt = generation
  const originatingStep = step.value
  
  // Handle digit keys (0-9)
  if (/^[0-9]$/.test(event.key)) {
    event.preventDefault()
    // Find first empty slot
    const emptyIndex = codeDigits.findIndex((d, i) => i < fieldsCount && d === '')
    if (emptyIndex !== -1) {
      codeDigits[emptyIndex] = event.key
      // Focus next input or stay on last if all filled
      const nextIndex = Math.min(emptyIndex + 1, fieldsCount - 1)
      codeInputs.value[nextIndex]?.focus()
      
      // Auto-submit if this was the last digit
      if (emptyIndex === fieldsCount - 1) {
        nextTick(() => {
          if (attempt !== generation || originatingStep !== step.value) return
          if (step.value === 'code') {
            handleCodeSubmit()
          } else if (step.value === 'approval') {
            handleApprovalSubmit()
          }
        })
      }
    }
  }
  
  // Handle Backspace - delete last filled digit
  if (event.key === 'Backspace') {
    // Find last filled slot within fields count
    const lastFilledIndex = codeDigits.slice(0, fieldsCount).reduce((acc, d, i) => d !== '' ? i : acc, -1)
    if (lastFilledIndex !== -1) {
      event.preventDefault()
      codeDigits[lastFilledIndex] = ''
      codeInputs.value[lastFilledIndex]?.focus()
    }
  }
  
  // Handle Enter - submit if complete
  if (event.key === 'Enter' && isCodeComplete.value) {
    event.preventDefault()
    if (step.value === 'code') {
      handleCodeSubmit()
    } else if (step.value === 'approval') {
      handleApprovalSubmit()
    }
  }
}

// Global paste handler for Ctrl+V / Cmd+V
const handleGlobalPaste = (event: ClipboardEvent) => {
  // Only respond when in code or approval step
  if (step.value !== 'code' && step.value !== 'approval') return
  if (!props.modelValue || isLoading.value) return
  
  // Don't intercept if user is focused on a different input outside modal
  const activeElement = document.activeElement
  const isInputElement = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
  const isModalElement = activeElement?.closest('.modal-content')
  
  // Allow if focused in modal or no other input is focused
  if (isInputElement && !isModalElement) return
  
  const paste = event.clipboardData?.getData('text')
  if (!paste) return
  const attempt = generation
  const originatingStep = step.value
  
  const numbers = paste.match(/\d/g)
  if (numbers) {
    event.preventDefault()
    const fieldsCount = codeInputs.value.length
    numbers.slice(0, fieldsCount).forEach((num, i) => {
      codeDigits[i] = num
    })
    codeInputs.value[Math.min(numbers.length, fieldsCount - 1)]?.focus()
    
    // Auto-submit if all fields are filled after paste
    if (numbers.length >= fieldsCount) {
      nextTick(() => {
        if (attempt !== generation || originatingStep !== step.value) return
        if (step.value === 'code') {
          handleCodeSubmit()
        } else if (step.value === 'approval') {
          handleApprovalSubmit()
        }
      })
    }
  }
}

// Add/remove global keyboard and paste listeners
onMounted(() => {
  if (props.modelValue && props.initialEmail) form.email = props.initialEmail
  window.addEventListener('keydown', handleGlobalKeydown)
  window.addEventListener('paste', handleGlobalPaste)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeydown)
  window.removeEventListener('paste', handleGlobalPaste)
})
</script>

<style scoped lang="scss">
// Screen reader only - visually hidden but accessible to browsers
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// Redirect loading overlay
.redirect-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--ds-overlay-85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.redirect-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.redirect-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--ds-accent-soft-15);
  border-top-color: var(--ds-accent);
  border-radius: 50%;
  animation: redirect-spin 0.8s linear infinite;
}

.redirect-text {
  font-size: var(--p-text-18);
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-text);
}

@keyframes redirect-spin {
  to {
    transform: rotate(360deg);
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--ds-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  // Keep the SDK's sibling captcha layer out of backdrop-filter compositing.
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}

.modal-overlay--inline {
  position: relative;
  inset: auto;
  width: 100%;
  height: auto;
  display: block;
  padding: 0;
  background: transparent;
  backdrop-filter: none;
  .modal-content {
    margin: 0 auto;
    max-height: none;
    overflow: visible;
  }
}

.modal-content {
  background: var(--ds-surface);
  border-radius: var(--ds-radius-modal);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  position: relative;
  box-shadow: 0 20px 40px var(--ds-scrim);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: var(--ds-text-size-h-md);
  color: var(--ds-text-muted);
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  
  &:hover {
    color: var(--ds-text-secondary);
  }
}

.modal-title {
  font-family: var(--ds-font-display);
  font-size: var(--p-text-28);
  letter-spacing: -0.02em;
  font-weight: var(--ds-weight-bold);
  text-align: center;
  margin-bottom: 16px;
  color: var(--ds-text);
}

.modal-subtitle {
  text-align: center;
  color: var(--ds-text-secondary);
  margin-bottom: 24px;
  line-height: 1.5;

  strong {
    color: var(--ds-text);
  }
}

.migration-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--ds-warning-soft);
  border: 1px solid #ffc107;
  border-radius: var(--ds-radius-card);
  margin-bottom: 20px;
  font-size: var(--ds-text-size-body);
  color: #856404;

  i {
    font-size: var(--p-text-18);
  }
}

.form-group {
  margin-bottom: 20px;
}

.email-input-container {
  position: relative;
}

.session-number {
  text-align: center;
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-text);
  margin-bottom: 12px;
  font-size: var(--ds-text-size-body);
  background: var(--ds-scrim);
  padding: 8px;
  border-radius: var(--ds-radius-control);
}

.form-label {
  display: block;
  font-weight: var(--ds-weight-semibold);
  margin-bottom: 8px;
  color: var(--ds-text);
}

.form-input {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid var(--ds-border);
  border-radius: var(--p-radius-50);
  background: var(--ds-overlay-04);
  color: var(--ds-text);
  font-size: var(--ds-text-size-body-lg);
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: var(--ds-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    box-shadow: var(--ds-focus-ring-shadow);
  }
}

.email-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--ds-surface-canvas);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  font-size: var(--ds-text-size-body-lg);

  span {
    color: var(--ds-text);
  }

  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-weight: var(--ds-weight-medium);
    font-size: var(--ds-text-size-body);

    &:hover {
      text-decoration: underline;
    }
  }
}

.code-digits {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.code-digit {
  width: 48px;
  height: 56px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  text-align: center;
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-text);
  background: var(--ds-surface-canvas);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    background: var(--ds-surface);
    box-shadow: 0 0 0 4px var(--ds-accent-soft-15);
  }
}

.resend-link {
  text-align: center;
  margin-top: 16px;

  a {
    color: var(--ds-text);
    text-decoration: none;
    font-size: var(--ds-text-size-body);

    &:hover:not(.disabled) {
      text-decoration: underline;
    }

    &.disabled {
      color: var(--ds-text-muted);
      cursor: not-allowed;
    }
  }
}

.password-input-wrapper {
  position: relative;
  
  .toggle-password {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--ds-text-muted);
    cursor: pointer;
    
    &:hover {
      color: var(--ds-text-secondary);
    }
  }

  .form-input {
    padding-right: 44px;
  }
}

.forgot-password {
  text-align: right;
  margin-top: 8px;
  
  a {
    color: var(--ds-text-muted);
    font-size: var(--ds-text-size-body);
    text-decoration: none;
    
    &:hover {
      color: var(--ds-text-secondary);
    }
  }
}

.form-error {
  margin-bottom: 16px;
  color: #d93025;
  font-size: var(--ds-text-size-body);
  line-height: 1.4;
}

.divider {
  margin: 24px 0;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: var(--ds-border);
    z-index: 0;
  }

  span {
    background: var(--ds-surface);
    padding: 0 12px;
    color: var(--ds-text-muted);
    position: relative;
    z-index: 1;
  }
}

.btn {
  padding: 14px;
  border-radius: var(--p-radius-50);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body-lg);
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-block {
  width: 100%;
  display: block;
}

.btn-primary {
  background: var(--ds-text);
  color: var(--ds-surface);
  
  &:hover:not(:disabled) {
    background: var(--ds-text-secondary);
  }
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--ds-border);
  color: var(--ds-text);

  &:hover:not(:disabled) {
    background: var(--ds-surface-raised);
  }
}

.social-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.social-btn {
  width: 60px;
  height: 48px;
  border-radius: var(--p-radius-50);
  border: 1px solid var(--ds-border);
  background: var(--ds-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-h-md);
  cursor: pointer;
  transition: background 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: var(--ds-surface-raised);
  }
}

.social-btn--wide {
  width: 100%;
  gap: 10px;
  padding: 0 16px;
}

.social-btn--wide span {
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
}

.social-btn.google,
.social-btn.telegram {
  background: var(--ds-surface);
  color: var(--ds-text-secondary);
  border: 1px solid #ddd;

  &:hover {
    background: var(--ds-surface-raised);
    border-color: #ccc;
  }

  i {
    font-size: var(--p-text-18);
  }
}

.social-btn.google i {
  color: var(--ds-accent);
}

.social-icon {
  width: 20px;
  height: 20px;
}

.social-btn.telegram i,
.social-btn.telegram .telegram-icon {
  color: #0088cc;
  fill: #0088cc;
  width: 18px;
  height: 18px;
}

.approval-form {
  .approval-info {
    text-align: center;
    margin-bottom: 24px;

    h3 {
      font-size: var(--p-text-22);
      margin-bottom: 8px;
    }

    p {
      color: #555;
      line-height: 1.5;
    }
    
    .approval-session {
      font-size: var(--p-text-13);
      color: #888;
      margin-top: 8px;
      background: var(--ds-surface-raised);
      padding: 4px 12px;
      border-radius: var(--ds-radius-card);
      display: inline-block;
    }
  }
  
  .code-input-wrapper {
    display: flex;
    gap: 8px;
    justify-content: center;
  }
  
  .code-digit {
    width: 42px;
    height: 52px;
    border: 1px solid var(--hm-border, var(--ds-border));
    border-radius: var(--p-radius-lg);
    text-align: center;
    font-size: var(--p-text-22);
    font-weight: var(--ds-weight-semibold);
    color: var(--hm-text-primary);
    background: var(--ds-surface-canvas);
    transition: all 0.2s;
    
    &:focus {
      outline: none;
      border-color: var(--ds-accent);
      background: var(--ds-surface);
      box-shadow: 0 0 0 3px var(--ds-accent-soft-10);
    }
  }
}

.approval-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;

  .btn {
    flex: 1;
  }
}

.auth-footer {
  text-align: center;
  margin-top: 24px;
  color: var(--ds-text-secondary);
  
  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-weight: var(--ds-weight-semibold);
  }

  &--links {
    a {
      color: var(--ds-text-secondary);
      font-weight: normal;
      text-decoration: underline;
    }

    .separator {
      margin: 0 8px;
      color: #ccc;
    }
  }
}

// Password requirements checklist
.password-requirements {
  margin-top: 12px;
  padding: 12px;
  background: var(--ds-surface-raised);
  border-radius: var(--ds-radius-control);
  
  .requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--p-text-13);
    color: var(--ds-text-secondary);
    padding: 4px 0;
    transition: color 0.2s ease;
    
    i {
      font-size: var(--ds-text-size-body-lg);
      color: #ccc;
      transition: color 0.2s ease;
    }
    
    &.valid {
      color: var(--ds-success);
      
      i {
        color: var(--ds-success);
      }
    }
  }
}

/* Hide captcha widget - bind mode shows popup on showCaptcha() call */
.captcha-hidden-container {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

// Email Not Found Confirmation Step
.email-not-found-icon {
  text-align: center;
  margin-bottom: 16px;
  
  i {
    font-size: var(--p-text-48);
    color: var(--ds-text-muted);
  }
}

.email-not-found-content {
  text-align: center;
  margin-bottom: 24px;

  .modal-subtitle {
    margin-bottom: 8px;
  }

  .email-highlight {
    font-weight: var(--ds-weight-semibold);
    font-size: var(--ds-text-size-body-lg);
    color: var(--hm-text-primary);
    padding: 12px 16px;
    background: var(--ds-surface-canvas);
    border-radius: var(--ds-radius-control);
    margin: 12px 0;
    word-break: break-all;
  }
}

.mt-12 {
  margin-top: 12px;
}
.modal-overlay--embedded {
  position: static;
  display: block;
  width: 100%;
  height: auto;
  background: none;
  backdrop-filter: none;
  z-index: auto;
  color: var(--entry-ink);
  .modal-content { padding: 0; max-width: none; max-height: none; overflow: visible; border: 0; border-radius: 0; background: none; box-shadow: none; }
  .modal-title { font-family: inherit; color: var(--entry-ink); font-size: 21px; margin: 0 0 16px; }
  .modal-subtitle, .form-label, .email-display, .password-requirements, .auth-footer { color: var(--entry-muted); }
  .form-label { font-size: 12px; }
  .form-input, .code-digit { background: var(--entry-input); border: 1px solid var(--entry-border); border-radius: 12px; color: var(--entry-ink); font-size: 16px; min-height: 54px; }
  .form-input::placeholder { color: var(--entry-muted); opacity: .7; }
  .btn-primary { background: var(--entry-ink); color: var(--entry-bg); border-color: var(--entry-ink); box-shadow: none; border-radius: 12px; min-height: 54px; padding: 14px 22px; }
  .btn-primary:hover:not(:disabled) { opacity: .88; transform: none; }
  .btn-outline { border-color: var(--entry-border); color: var(--entry-ink); border-radius: 12px; min-height: 44px; }
  .form-input:focus, .code-digit:focus { border-color: var(--entry-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--entry-accent) 15%, transparent); }
  .btn:focus-visible, .toggle-password:focus-visible { outline: 2px solid var(--entry-accent); outline-offset: 3px; }
  .email-display { border-color: var(--entry-border); background: var(--entry-input); }
  .email-display a, .resend-link a, .auth-footer a { color: var(--entry-accent); }
  .form-error { color: var(--ds-danger); }
  &.modal-overlay--email .auth-form { display: grid; grid-template-columns: 1fr auto; align-items: end; gap: 10px; }
  &.modal-overlay--email .form-group { margin: 0; min-width: 0; }
  &.modal-overlay--email .form-error { grid-column: 1 / -1; grid-row: 2; margin: 0; }
  &.modal-overlay--email .btn { grid-column: 2; grid-row: 1; width: auto; }
  @media (max-width: 420px) {
    &.modal-overlay--email .auth-form { grid-template-columns: 1fr; }
    &.modal-overlay--email .btn { grid-column: 1; grid-row: auto; width: 100%; }
    &.modal-overlay--email .form-error { grid-row: auto; }
  }
}
</style>
